var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getCacheItem, setCacheItem } from '../cache';
import { getRequestId, updateRequest } from '../dataLayer';
import { executeWhenIdle, compareObjectFields } from '../utils';
export const MNTN_CACHE_KEY = 'mntnData';
export const MNTN_WINDOW_KEY = 'mntn';
export const DEFAULT_LISTENER_TIMEOUT = 3000;
const EMPTY_MNTN_DATA = {
    is_viewable_verified_visit: false,
    creative_group_id: '',
    creative_group_name: '',
    impression_epoch_micros: 0,
    is_cross_device: false,
    visit_tracking: '',
    requestId: '',
};
/**
 * Gets a safe copy of cached MNTN data or returns defaults
 */
function getSafeMntnCache() {
    const stored = getCacheItem(MNTN_CACHE_KEY);
    if (!stored) {
        return Object.assign({}, EMPTY_MNTN_DATA);
    }
    return {
        is_viewable_verified_visit: typeof stored.is_viewable_verified_visit === 'string'
            ? stored.is_viewable_verified_visit === 'true'
            : !!stored.is_viewable_verified_visit,
        creative_group_id: typeof stored.creative_group_id === 'string' ? stored.creative_group_id : '',
        creative_group_name: typeof stored.creative_group_name === 'string' ? stored.creative_group_name : '',
        impression_epoch_micros: stored.impression_epoch_micros || new Float32Array(),
        is_cross_device: !!stored.is_cross_device,
        visit_tracking: typeof stored.visit_tracking === 'string' ? stored.visit_tracking : '',
        requestId: typeof stored.requestId === 'string' ? stored.requestId : '',
    };
}
/**
 * Extract MNTN data from the window object
 */
export function getMntnData() {
    try {
        const mntnObj = window.mntn;
        if (!mntnObj) {
            return null;
        }
        const mntnData = {
            creative_group_id: mntnObj.creative_group_id || '',
            creative_group_name: mntnObj.creative_group_name || '',
            impression_epoch_micros: mntnObj.impression_epoch_micros || new Float32Array(),
            is_cross_device: !!mntnObj.is_cross_device,
            visit_tracking: mntnObj.visit_tracking || '',
            requestId: '',
        };
        return mntnData;
    }
    catch (error) {
        console.error('[MntnCapture] Error extracting MNTN data:', error);
        return null;
    }
}
/**
 * Compare two MNTN data objects for equality
 */
export function areMntnObjectsEqual(obj1, obj2) {
    return compareObjectFields(['creative_group_id', 'creative_group_name', 'visit_tracking'], obj1, obj2);
}
/**
 * Send MNTN data to MAPI if it exists and has changed
 */
export function sendMntnData(requestId) {
    try {
        const mntnData = getMntnData();
        if (!mntnData || !mntnData.creative_group_id || !mntnData.creative_group_name || !mntnData.visit_tracking) {
            return;
        }
        mntnData.requestId = requestId;
        const cache = getSafeMntnCache();
        if (cache.requestId === requestId && areMntnObjectsEqual(cache, mntnData)) {
            return;
        }
        const doNetworkCall = () => {
            updateRequest(requestId, { mntn: mntnData })
                .then(() => setCacheItem(MNTN_CACHE_KEY, mntnData))
                .catch(error => {
                console.error('[MntnCapture] Update request failed:', error);
            });
        };
        executeWhenIdle(doNetworkCall);
    }
    catch (error) {
        console.error('[MntnCapture] Send data error:', error);
    }
}
/**
 * Efficiently detects when MNTN data becomes available using RAF and timeout
 * @param onFound - Callback for when MNTN data is found
 * @param maxDuration - Maximum time to keep checking (in ms)
 * @returns A cleanup function
 */
export function detectMntnData(onFound, maxDuration = 3000) {
    const endTime = performance.now() + maxDuration;
    let active = true;
    const initialData = getMntnData();
    if (initialData) {
        onFound(initialData);
        return () => { };
    }
    function checkWithRAF() {
        if (!active)
            return;
        const data = getMntnData();
        if (data) {
            active = false;
            onFound(data);
            return;
        }
        if (performance.now() < endTime) {
            requestAnimationFrame(checkWithRAF);
        }
        else {
            active = false;
        }
    }
    requestAnimationFrame(checkWithRAF);
    return () => {
        active = false;
    };
}
/**
 * Handle MNTN data detection and send to API
 */
export function handleMntnData() {
    return __awaiter(this, arguments, void 0, function* (listenerTimeout = DEFAULT_LISTENER_TIMEOUT) {
        return new Promise(resolve => {
            const requestId = getRequestId();
            if (!requestId) {
                console.warn('[MntnCapture] No request ID available');
                resolve(null);
                return;
            }
            const mntnData = getMntnData();
            if (mntnData) {
                sendMntnData(requestId);
                resolve(mntnData);
                return;
            }
            const removeDetector = detectMntnData(data => {
                sendMntnData(requestId);
                resolve(data);
            }, 3000);
            setTimeout(() => {
                removeDetector();
                resolve(getMntnData());
            }, listenerTimeout);
        });
    });
}
/**
 * Main entry point for MNTN data capture
 * Uses single check + timeout instead of continuous polling
 *
 * @param maxWaitMs - How long to wait for requestId before giving up
 * @param listenerTimeout - How long to keep the detector active
 */
export function run() {
    return __awaiter(this, arguments, void 0, function* (maxWaitMs = 1000, listenerTimeout = DEFAULT_LISTENER_TIMEOUT) {
        const requestId = getRequestId();
        const mntnData = getMntnData();
        if (requestId && mntnData) {
            sendMntnData(requestId);
            return mntnData;
        }
        if (requestId) {
            return handleMntnData(listenerTimeout);
        }
        return new Promise(resolve => {
            const startTime = performance.now();
            const endTime = startTime + maxWaitMs;
            function checkRequestId() {
                const currentRequestId = getRequestId();
                if (currentRequestId) {
                    handleMntnData(listenerTimeout).then(resolve);
                    return;
                }
                if (performance.now() >= endTime) {
                    console.warn('[MntnCapture] Timed out waiting for request ID');
                    resolve(null);
                    return;
                }
                requestAnimationFrame(checkRequestId);
            }
            requestAnimationFrame(checkRequestId);
        });
    });
}
//# sourceMappingURL=MntnCapture.js.map